import React, {
    useContext, useCallback, useState, useEffect, useMemo
} from 'react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { ModalNextProvider } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import {
    ViewModelServiceContext,
    withViewModelService,
    ViewModelForm
} from 'gw-portals-viewmodel-react';
import {
    withApplicationPropertyContext
} from 'tdic-components-platform-react';
import { WizardPage, wizardProps, WizardPageTemplate } from 'gw-portals-wizard-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { isCapabilityEnabled } from 'gw-portals-config-js';
import { messages as commonMessages } from 'gw-platform-translations';
import { TermsAndConditionUtils } from 'gw-portals-util-js';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import ClaimUtil from '../../utils/ClaimUtil';
import metadata from './SummaryPage.metadata.json5';
import summaryStyles from './SummaryPage.module.scss';
import AdjusterContent from './Component/AdjusterContent';
import AddNotePopup from './AddNotePopup';
import messages from '../../FNOL.messages';

function FNOLSummaryPage(props) {
    const translator = useContext(TranslatorContext);
    const { FNOLService } = useDependencies('FNOLService');
    const { ClaimService } = useDependencies('ClaimService');
    const viewModelService = useContext(ViewModelServiceContext);
    const { tncTransactionTypesSubTypes, termData: terms } = props;
    const {
        wizardData: claimVM,
        updateWizardData,
        authHeader,
        user_name: userName,
        email,
        history,
        history: {
            location: { state }
        }
    } = props;
    const { capabilitiesConfig } = appConfig;
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [popupNotesVM, updatePopupNotesVM] = useState(null);
    const [acceptTC, setAcceptTC] = useState(false);

    const newNoteViewModelObj = useCallback((initialData) => {
        return {
            note: viewModelService.create(
                initialData.note,
                'cc',
                'edge.capabilities.note.dto.NoteDTO'
            )
        };
    }, [viewModelService]);

    const getWhereLocation = useCallback(() => {
        if (
            !_.isEmpty(claimVM.lossLocation_Ext.value)
            && claimVM.lossLocation_Ext.addressLine1.value
            && claimVM.lossLocation_Ext.city.value
            && claimVM.lossLocation_Ext.state.value.code
            && claimVM.lossLocation_Ext.postalCode.value
        ) {
            return `${claimVM.lossLocation_Ext.addressLine1.value},
                ${claimVM.lossLocation_Ext.city.value},
                ${claimVM.lossLocation_Ext.state.value.code},
                ${claimVM.lossLocation_Ext.postalCode.value}`;
        }
        return '';
    }, [claimVM]);

    const isWorkersCompPolicy = useCallback(() => {
        return _.get(claimVM, 'value.policy.policyType') === 'WC7WorkersComp';
    }, [claimVM]);

    const getPrimaryPhoneNumber = useCallback((accountContact) => {
        const contactData = isWorkersCompPolicy() ? _.get(claimVM, 'value.contacts[0]') : accountContact;
        const { primaryPhoneType } = contactData;
        if (primaryPhoneType === 'work') {
            return _.get(contactData, 'workNumber', '');
        } if (primaryPhoneType === 'home') {
            return _.get(contactData, 'homeNumber', '');
        }
        if (primaryPhoneType === 'mobile') {
            return _.get(contactData, 'cellNumber', '');
        }
        return null;
    }, [claimVM, isWorkersCompPolicy]);


    const getContactPerson = useCallback(() => {
        const displayName = _.get(claimVM.value, 'mainContact.displayName', '');
        if (_.isEmpty(displayName)) {
            const firstName = _.get(claimVM.value, 'mainContact.firstName', '');
            const lastName = _.get(claimVM.value, 'mainContact.lastName', '');
            return `${firstName} ${lastName}`;
        }
        return displayName;
    }, [claimVM.value]);

    const getEmployeeFirstName = useCallback(() => {
        if (isWorkersCompPolicy()) {
            return _.get(claimVM, 'value.lobs.wc7workersComp_Ext.injuryIncident.injured.firstName', '');
        }
        return _.get(claimVM, 'value.contacts[0].firstName', '');
    }, [claimVM, isWorkersCompPolicy]);

    const getEmployeeLastName = useCallback(() => {
        if (isWorkersCompPolicy()) {
            return _.get(claimVM, 'value.lobs.wc7workersComp_Ext.injuryIncident.injured.lastName', '');
        }
        return _.get(claimVM, 'value.contacts[0].lastName', '');
    }, [claimVM, isWorkersCompPolicy]);

    const getEmployeeEmailAddress = useCallback(() => {
        if (isWorkersCompPolicy()) {
            return _.get(claimVM, 'value.lobs.wc7workersComp_Ext.injuryIncident.injured.emailAddress1', '');
        }
        return _.get(claimVM.value, 'mainContact.emailAddress1', '');
    }, [claimVM, isWorkersCompPolicy]);

    const getEmployeePhone = useCallback(() => {
        if (isWorkersCompPolicy()) {
            return _.get(claimVM, 'value.lobs.wc7workersComp_Ext.injuryIncident.injured.cellNumber', '');
        }
        return getPrimaryPhoneNumber(claimVM.value.mainContact);
    }, [claimVM, getPrimaryPhoneNumber, isWorkersCompPolicy]);

    const isVendorRepairOptionEnabled = useMemo(() => {
        const lossCause = _.get(claimVM.value, 'lossCause');
        return !_.isNil(_.get(claimVM.value, 'lobs.personalAuto'))
            && (lossCause !== 'theftentire')
            && isCapabilityEnabled({ capabilitiesConfig, capabilityName: 'vendor' });
    }, [capabilitiesConfig, claimVM]);

    const repairFacilityVisible = useCallback(() => {
        return isVendorRepairOptionEnabled && !_.isNil(_.get(claimVM.value, 'lobs.personalAuto.repairOption'));
    }, [claimVM, isVendorRepairOptionEnabled]);

    const getRepairFacility = useCallback(() => {
        const repairOptionChoice = _.get(claimVM.value, 'lobs.personalAuto.repairOption.repairOptionChoice');
        if (!_.isNil(repairOptionChoice) && repairOptionChoice === 'NoRepair') {
            return translator(messages.fnolNoRepairRequested);
        }
        const repairFacility = _.get(claimVM.value, 'lobs.personalAuto.repairOption.repairFacility');
        if (_.isNil(repairFacility)) {
            return null;
        }
        const vendorName = repairFacility.contactName ? repairFacility.contactName : '';
        const vendorAddress = repairFacility.primaryAddress;
        const addressDetails = {
            addressLine1: vendorAddress.addressLine1,
            addressLine2: vendorAddress.addressLine2,
            addressLine3: vendorAddress.addressLine3,
            city: vendorAddress.city,
            state: vendorAddress.state,
            postalCode: vendorAddress.postalCode
        };

        const formattedVendorAddress = Object.values(addressDetails)
            .filter((data) => data)
            .join(', ');
        return (
            <div>
                <p>{vendorName}</p>
                <p>{formattedVendorAddress}</p>
            </div>
        );
    }, [claimVM, translator]);
    const initializeNotesVM = useCallback(() => {
        if (!_.isEmpty(claimVM.adjusterNote.value)) {
            const getNotesVM = newNoteViewModelObj({
                note: {
                    claimNumber: claimVM.claimNumber.value,
                    subject: claimVM.adjusterNote.subject.value,
                    body: claimVM.adjusterNote.body.value
                }
            });
            return updatePopupNotesVM(getNotesVM.note);
        }

        const getNotesVM = newNoteViewModelObj({
            note: {
                claimNumber: claimVM.claimNumber.value,
                subject: '',
                body: ''
            }
        });
        return updatePopupNotesVM(getNotesVM.note);
    }, [claimVM, newNoteViewModelObj]);
    useEffect(() => {
        initializeNotesVM();
        // execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const showModal = useCallback(
        async (getNotesVM) => {
            const componentProps = {
                title: translator(messages.dialogAddNoteToAdjuster),
                actionBtnLabel: messages.dialogOk,
                cancelBtnLabel: messages.dialogCancel,
                notesVM: getNotesVM,
                viewModelService: viewModelService
            };

            const results = await ModalNextProvider.showModal(
                <AddNotePopup {...componentProps} />
            );

            return results;
        },
        [translator, viewModelService]
    );

    const updateNoteData = useCallback(
        (noteVM) => {
            setIsLoading(true);
            const claimNumber = _.get(claimVM.value, 'claimNumber');
            return ClaimService.updateClaimNote(claimNumber, noteVM.value, authHeader)
                .then((response) => {
                    _.set(claimVM, 'adjusterNote.value', response);
                    updateWizardData(claimVM);
                    _.set(noteVM, 'value', response);
                    updatePopupNotesVM(noteVM);
                })
                .catch(() => {
                    ModalNextProvider.showAlert({
                        title: messages.fnolNoteErrorHead,
                        message: messages.fnolNoteErrorMessage,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).catch(_.noop);
                    return false;
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [claimVM, authHeader, updateWizardData, ClaimService]
    );

    const handleEditNote = useCallback(() => {
        showModal(popupNotesVM).then((updatedVM) => {
            const modifiedVM = viewModelService.clone(updatedVM);
            updateNoteData(modifiedVM);
        }).catch(_.noop);
    }, [popupNotesVM, showModal, updateNoteData, viewModelService]);

    const handleDeleteNote = useCallback(() => {
        setIsLoading(true);
        const claimNumber = _.get(claimVM.value, 'claimNumber');
        const noteId = _.get(claimVM.value, 'adjusterNote.publicID');
        return ClaimService.deleteClaimNote(claimNumber, noteId, authHeader)
            .then((response) => {
                _.set(claimVM, 'adjusterNote.value', response);
                updateWizardData(claimVM);
                _.set(popupNotesVM, 'value', response);
                updatePopupNotesVM(popupNotesVM);
            })
            .catch(() => {
                ModalNextProvider.showAlert({
                    title: messages.fnolNoteErrorHead,
                    message: messages.fnolNoteErrorMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                }).catch(_.noop);
                return false;
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [claimVM, authHeader, popupNotesVM, updateWizardData, ClaimService]);

    const saveNoteData = useCallback(
        (noteVM) => {
            setIsLoading(true);
            const claimNumber = _.get(claimVM.value, 'claimNumber');
            return ClaimService.createClaimNote(claimNumber, noteVM.value, authHeader)
                .then((response) => {
                    _.set(claimVM, 'adjusterNote.value', response);
                    updateWizardData(claimVM);
                    _.set(noteVM, 'value', response);
                    updatePopupNotesVM(noteVM);
                })
                .catch(() => {
                    ModalNextProvider.showAlert({
                        title: messages.fnolNoteErrorHead,
                        message: messages.fnolNoteErrorMessage,
                        status: 'error',
                        icon: 'mi-error-outline',
                        confirmButtonText: commonMessages.ok
                    }).catch(_.noop);
                    return false;
                })
                .finally(() => {
                    setIsLoading(false);
                });
        },
        [authHeader, claimVM, updateWizardData, ClaimService]
    );

    const addNotesToAdjuster = useCallback(() => {
        showModal(popupNotesVM).then((updatedVM) => {
            const modifiedVM = viewModelService.clone(updatedVM);
            saveNoteData(modifiedVM);
        }).catch(_.noop);
    }, [popupNotesVM, saveNoteData, showModal, viewModelService]);

    const getWhereLocationVisibility = useCallback(() => {
        if (!_.isEmpty(claimVM.lossLocation_Ext.value)) {
            return true;
        }
        return false;
    }, [claimVM]);

    const onNext = useCallback(() => {
        setIsLoading(true);
        setLoadingMessage(translator(messages.fnolSubmitClaimDetails));
        const requestPayload = _.get(claimVM.value.policy, ['policyType']) === 'WC7WorkersComp'
            ? ClaimUtil.prepareWC7Request(claimVM.value) : claimVM.value;
        _.set(requestPayload, 'portalClaimSubmitted', 'Yes');
        _.omit(claimVM.value, 'mainContact.publicID');
        _.omit(claimVM.value, 'mainContact.primaryAddress.publicID');
        return FNOLService.saveFNOLDetails(requestPayload, authHeader)
            .then((response) => {
                claimVM.value = response;
                return TermsAndConditionUtils.saveTnCAcceptance(_.get(terms, 'Version', ''), tncTransactionTypesSubTypes, 'Fnol', 'Fnol', authHeader, _.get(claimVM.value, 'claimNumber'), userName, email);
            })
            .then((result) => {
                if (result) {
                    const claimNumber = _.get(claimVM.value, 'claimNumber');
                    const { redirectPath } = state;
                    return history.push({
                        pathname: `/confirmation/${claimNumber}`,
                        state: {
                            redirectPath: redirectPath
                        }
                    });
                }
                return null;
            })
            .catch(() => {
                ModalNextProvider.showAlert({
                    title: messages.fnolErrorSubmitClaimTitle,
                    message: messages.fnolErrorSubmitClaimMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                }).catch(_.noop);
                return false;
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [claimVM, translator, FNOLService, authHeader, terms,
        tncTransactionTypesSubTypes, userName, email, state, history]);

    const typeOfIncident = _.get(claimVM, 'lossCause.value.name');

    const overrides = {
        fnolTypeOfIncident: {
            value: translator({
                id: typeOfIncident,
                defaultMessage: typeOfIncident
            })
        },
        addNotebtnDiv: {
            visible: _.isEmpty(claimVM.adjusterNote.value)
        },
        adjusterNoteDiv: {
            visible: !_.isEmpty(claimVM.adjusterNote.value)
        },
        fnolWhere: {
            visible: getWhereLocationVisibility(),
            value: getWhereLocation()
        },
        fnolContactPerson: {
            visible: !isWorkersCompPolicy(),
            value: getContactPerson()
        },
        fnolPhoneNumber: {
            value: getPrimaryPhoneNumber(claimVM.value.mainContact)
        },
        summaryPageLoader: {
            loaded: !isLoading,
            loadingMessage: translator(loadingMessage)
        },
        fnolSummaryPage: {
            visible: !isLoading
        },
        fnolRepairFacility: {
            visible: repairFacilityVisible(),
            value: repairFacilityVisible() ? getRepairFacility() : ''
        },
        adjusterContent: {
            handleEditNote,
            handleDeleteNote,
            claimVM
        },
        tncAcceptance: {
            onValueChange: setAcceptTC,
            value: acceptTC,
            label: messages.tncAcceptance
        },
        fnolReportedBy: {
            value: _.get(claimVM, 'value.relatedContacts[0].contact.displayName', '')
        },
        fnolWC7EmployeeFirstName: {
            visible: isWorkersCompPolicy(),
            value: getEmployeeFirstName()
        },
        fnolWC7EmployeeLastName: {
            visible: isWorkersCompPolicy(),
            value: getEmployeeLastName()
        },
        fnolWC7EmployeePhone: {
            visible: isWorkersCompPolicy(),
            value: getEmployeePhone()
        },
        fnolEmailAddress: {
            value: getEmployeeEmailAddress()
        },
        fnolDate: {
            value: _.get(claimVM.value, 'typeOfClaim_Ext') === 'workerscompensation'
                ? _.get(claimVM.value, 'lobs.wc7workersComp_Ext.dateReportedToEmployer')
                : _.get(claimVM.value, 'reportedDate_Ext')
        },
        fnolPolicyType: {
            value: _.get(claimVM.value, 'policy.policyTypeDisplayName_Ext')
        }
    };
    const resolvers = {
        resolveCallbackMap: {
            addNotesToAdjuster: addNotesToAdjuster
        },
        resolveComponentMap: {
            adjustercontent: AdjusterContent
        },
        resolveClassNameMap: summaryStyles
    };

    const readValue = (id, path) => {
        return readViewModelValue(metadata.pageContent, claimVM, id, path, overrides);
    };

    return (
        <WizardPage
            nextLabel={translator(messages.fnolSubmitClaim)}
            cancelLabel={translator(messages.fnolSaveandExit)}
            onNext={onNext}
            template={WizardPageTemplate}
            disableNext={!acceptTC}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrides}
                onModelChange={updateWizardData}
                resolveValue={readValue}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </WizardPage>
    );
}

FNOLSummaryPage.propTypes = wizardProps;
FNOLSummaryPage.propTypes = {
    history: PropTypes.shape({
        push: PropTypes.func
    }).isRequired
};
export default withRouter(withAuthenticationContext(
    withApplicationPropertyContext(withViewModelService(FNOLSummaryPage))
));
